import {withTrailingSlash} from 'ufo';
import {useGuestHost, useLogger, useUniversalFetchRaw} from '#imports';
import type {MicroFrontendAvailableApps} from '@malt/nuxt-micro-frontend-types';
import {MicroFrontendEndpointMapper, MicroFrontendRootIdMapper, MicroFrontendBuildAssetsDirMapper} from '@malt/nuxt-micro-frontend-types';

export function useMicroFrontend(
    microFrontendApp: MicroFrontendAvailableApps,
    targetSelector: () => HTMLElement | null | undefined = () => document.querySelector('body'),
) {
    const logger = useLogger();
    const host = useGuestHost();

    let microFrontendContent: Document;

    const hostHead = document?.querySelector('head');
    const hostBody = document?.querySelector('body');

    const mfLinks: HTMLLinkElement[] = [];
    const mfScripts: Record<'head' | 'body', HTMLScriptElement[]> = {
        head: [],
        body: [],
    };

    const appEndpoint = MicroFrontendEndpointMapper[microFrontendApp];
    const appRootId = MicroFrontendRootIdMapper[microFrontendApp];
    const appBuildAssetsDir = MicroFrontendBuildAssetsDirMapper[microFrontendApp];

    async function getAppHtmlContent(microFrontendURL: string): Promise<string | null> {
        const response = await useUniversalFetchRaw(microFrontendURL, {
            method: 'GET',
            credentials: 'same-origin',
            redirect: 'error',
        });
        if (response.status >= 300 && response.status !== 401 && response.status !== 403) {
            return Promise.reject(new Error('Failed to GET ' + microFrontendURL + ' (status: ' + response.status + ')'));
        }
        if (!response.ok) {
            return null;
        }
        const htmlString = (await response._data) as string;
        if (!htmlString) {
            return null;
        }
        return htmlString;
    }

    function isLinkRelevant(link: HTMLLinkElement) {
        return link.rel === 'stylesheet' && link.href.includes(appBuildAssetsDir);
    }

    function isScriptRelevant(script: HTMLScriptElement) {
        return (
            ((script.type === 'text/javascript' || script.type === 'module') && script.src.includes(appBuildAssetsDir)) ||
            script.hasAttribute('data-nuxt-data') ||
            script.hasAttribute('data-nuxt-logs') ||
            (script.textContent || '').includes(`window.__NUXT__["${microFrontendApp}"]`)
        );
    }

    async function fetchMicroFrontendAssets() {
        cleanMicroFrontendContext();

        const microFrontendURL = withTrailingSlash(`${host}${appEndpoint}`);

        if (checkIfMicroFrontendIsLoaded()) {
            return;
        }

        const htmlString = await getAppHtmlContent(microFrontendURL);

        if (htmlString) {
            const parser = new DOMParser();
            microFrontendContent = parser.parseFromString(htmlString, 'text/html');
        } else {
            throw new Error('The Micro-Frontend endpoint did not return any content');
        }

        if (microFrontendContent) {
            Array.from(microFrontendContent.querySelectorAll('link'))
                .filter(isLinkRelevant)
                .forEach((link) => {
                    mfLinks.push(link);
                });
            Array.from(microFrontendContent.querySelectorAll('script'))
                .filter(isScriptRelevant)
                .forEach((script) => {
                    // For security reasons, modern browsers won't just let us dump scripts coming from elsewhere, hence the copy.
                    const scriptCopy = document.createElement('script');

                    // Element.getAttributeNames isn't supported by Samsung Internet < 8.2
                    for (let i = 0, attrs = script.attributes, len = attrs.length; i < len; i++) {
                        const attr = attrs[i];
                        if (attr) {
                            scriptCopy.setAttribute(attr.name, attr.value);
                        }
                    }

                    // Dynamically added scripts are async by default.
                    // We don't want them to be async unless specified (so that scripts order is assured)
                    if (!scriptCopy.getAttribute('async')) {
                        scriptCopy.async = false;
                    }

                    // https://developer.mozilla.org/en-US/docs/Web/API/Element/getAttribute#retrieving_nonce_values
                    // For security reasons, CSP nonces from non-script sources, such as CSS selectors
                    // and `.getAttribute("nonce")` calls are hidden.
                    if (script.nonce) {
                        scriptCopy.nonce = script.nonce;
                    }

                    if (!script.src && script.innerHTML) {
                        scriptCopy.innerHTML = script.innerHTML;
                    }

                    try {
                        if (script.parentNode?.nodeName === 'HEAD') {
                            mfScripts.head.push(scriptCopy);
                        } else {
                            mfScripts.body.push(scriptCopy);
                        }
                    } catch (e) {
                        /* SyntaxError is very likely caused by a duplicate script that is re-declaring an identifier.
                         * Skip to next script.
                         */
                        if (!(e instanceof SyntaxError)) {
                            throw e;
                        }
                    }
                });
        }
    }

    /**
     * Clean Nuxt content from unctx to avoid context conflict.
     * @param isUnmount - if true, will also delete the MF from window.__NUXT__. Is true only when MF `onBeforeUnmount` event is triggered.
     *
     * Sometimes Nuxt doesn't clean context from window.__NUXT__ soon enough, so we need to do it manually
     *
     * Special cases:
     * - Client homepage (/project-client/find-freelancers): 3 MFs loaded at the same time
     * - Admin project: Each tab is the same MF (so same appID), but with different props.
     */
    function cleanMicroFrontendContext(isUnmount = false) {
        try {
            const unctx = (window as any).__unctx__;
            if (unctx) {
                const appInstance = unctx.get(microFrontendApp);
                if (appInstance) {
                    appInstance.unset();
                }
            }
            if (isUnmount && (window as any).__NUXT__[microFrontendApp]) {
                delete (window as any).__NUXT__[microFrontendApp];
            }
        } catch (error) {
            logger.warn('Failed to clean microfrontend context during cleanup', {microFrontendApp, error});
        }
    }

    function injectMicroFrontendAssets() {
        // Remove maybe uncleaned MF Nuxt app instance
        cleanMicroFrontendContext();

        if (checkIfMicroFrontendIsLoaded()) {
            return;
        }

        const domContent = microFrontendContent?.querySelector(`#${appRootId}`);
        const hostTarget = targetSelector();

        if (domContent && microFrontendContent) {
            [...microFrontendContent.documentElement.attributes].forEach((attr) => {
                document.documentElement.setAttribute(attr.nodeName === 'id' ? 'data-id' : attr.nodeName, attr.nodeValue as any);
            });

            if (hostTarget) {
                hostTarget.appendChild(domContent);

                mfLinks.forEach((link) => {
                    link.setAttribute('data-mf-id', microFrontendApp);
                    hostHead?.appendChild(link);
                });

                const augmentScript = (script: HTMLScriptElement) => {
                    script.setAttribute('data-mf-id', microFrontendApp);
                    if (script.src) {
                        script.src = `${toReexecutableEntrySrc(script.src)}?${new Date().getTime()}`;
                    }
                };

                mfScripts.head.forEach((script) => {
                    augmentScript(script);
                    hostHead?.appendChild(script);
                });
                mfScripts.body.forEach((script) => {
                    augmentScript(script);
                    hostBody?.appendChild(script);
                });
            } else {
                logger.error(`Can't find target element to inject Micro-Frontend`);
            }
        }
    }

    function toReexecutableEntrySrc(src: string) {
        // In dev, Nuxt serves entry.async.js, whose cached import of entry.js never remounts the MF when it is injected again.
        return import.meta.dev ? src.replace(/\/nuxt\/dist\/app\/entry\.async\.js$/, '/nuxt/dist/app/entry.js') : src;
    }

    function cleanMicroFrontendAssets() {
        // Send custom event to unmount MF Vue app
        const destroy = new CustomEvent(`${microFrontendApp}:mf:destroy`);
        window.dispatchEvent(destroy);

        // Remove MF Nuxt app instance
        cleanMicroFrontendContext(true);

        // Remove MF wrapper element
        document.querySelector(`#${microFrontendApp}`)?.remove();

        // Remove all MF related resources
        document.querySelectorAll(`[data-mf-id="${microFrontendApp}"]`).forEach((element) => {
            element.remove();
        });
    }

    function checkIfMicroFrontendIsLoaded() {
        // Maybe using window.__NUXT__ is not the best way to check if the MF is loaded, so we use the script injection as a fallback.
        const isStoredInWindow = (window as any).__NUXT__?.[microFrontendApp] !== undefined;

        const scriptIsInjectedInDOM = document.querySelector(`script[data-mf-id="${microFrontendApp}"]`) !== null;
        const elementIsInjectedInDOM = document.querySelector(`#${microFrontendApp}`) !== null;
        return isStoredInWindow || (scriptIsInjectedInDOM && elementIsInjectedInDOM);
    }

    return {
        fetchMicroFrontendAssets,
        injectMicroFrontendAssets,
        cleanMicroFrontendAssets,
    };
}
